/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { Fragment, useContext, useEffect, useState } from "react";
import { Button, TextField, MenuItem, Select } from "@mui/material";
import { useTranslation } from "react-i18next";
import LabelledField from "components/labelled-field";
import { TabPanel } from "cms/images/image-selector/dialog-edit/panels/tab-panel";
import { DocumentEditContext } from "cms/documents/document-selector/context/document-edit-context";
import { SVGIcons } from "cms/icons/svg-icons";
import { useSnackbar } from "hooks/useSnackbar";


const DocumentEditRecordsManagementPanel = () => {
    const { t }  = useTranslation();
    const {
        hasDocument,
        selectedDocument,
        localState,
        setLocalState,
        setDocument,
        selectedDocEditTab,
        setHasChanges
    } = useContext(DocumentEditContext);
    const { showSnackbar } = useSnackbar();

    const {
        classification: _classification = "",
        sub_classification: _sub_classification,
        owner: _owner,
        external_id: _external_id,
        doc_id: _doc_id,
        retention_months: _retention_months,
    } = selectedDocument || {};
    const [classification,setClassification] = useState(_classification || "");
    const [sub_classification, setSub_classification] = useState(_sub_classification || "");
    const [owner,setOwner] = useState(_owner ? _owner : "");
    const [external_id, setExternal_id] = useState(_external_id || "");
    const [retention_months, setRetention_months] = useState(_retention_months || 84);

    useEffect(() => {
        setLocalState({
            document: selectedDocument,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDocument]);
    
    const updateDocument = (change) => {
        const updatedDocument = {
            ...localState?.document,
            ...change
        };
        setDocument(updatedDocument);
        setLocalState({
            document: updatedDocument,
        });
    };
    
    const onChange = (value, property) => {
        const change = {
            [property]: value
        };
        updateDocument(change);
        setHasChanges(true);
    };
    
    const onChangeClassification = (event) => {
        const value = event.target.value;
        setClassification(value);
        onChange(value, "classification");
    };
    const onChangeSubClassification = (event) => {
        const value = event.target.value;
        setSub_classification(value);
        onChange(value, "sub_classification");
    };
    const onChangeOwner = (event) => {
        const value = event.target.value;
        setOwner(value);
        onChange(value, "owner");
    };
    const onChangeExternalId = (event) => {
        const value = event.target.value;
        setExternal_id(value);
        onChange(value, "external_id");
    };
    const onChangeRetentionMonths = (event) => {
        const value = event.target.value;
        setRetention_months(value);
        onChange((Number(value)), "retention_months");
    };
    const onCopyToClipbaord = () => {
        navigator.clipboard.writeText(_doc_id);
        showSnackbar("Copied to system clipboard");
        // 
    };
    return (
        <Fragment>
            <TabPanel value={selectedDocEditTab} index={1}>
                {hasDocument && (
                    <>
                        <LabelledField label={t("commons.classification")} className='labelField document-edit-panel'>
                            <Select
                                onChange={onChangeClassification}
                                id='document-classification'
                                fullWidth
                                variant="outlined"
                                value={classification}
                                displayEmpty
                                renderValue={(selected) => {
                                    if (selected.length === 0) {
                                        return <span className="select-placeholder-css">Select a classification</span>;
                                    }
                                    return selected;
                                }}
                            >
                                <MenuItem defaultChecked value='Administration'>Administration</MenuItem>
                                <MenuItem value='Council, Boards and By-laws'>Council, Boards and By-laws</MenuItem>
                                <MenuItem value='Development and Planning'>Development and Planning</MenuItem>
                                <MenuItem value='Environmental Services'>Environmental Services</MenuItem>
                                <MenuItem value='Finance and Account'>Finance and Account</MenuItem>
                                <MenuItem value='Human Resources'>Human Resources</MenuItem>
                                <MenuItem value='Justice'>Justice</MenuItem>
                                <MenuItem value='Legal Affairs'>Legal Affairs</MenuItem>
                                <MenuItem value='Media and Public Relations'>Media and Public Relations</MenuItem>
                                <MenuItem value='Protection and Enforcement Services'>Protection and Enforcement Services</MenuItem>
                                <MenuItem value='Recreation and Culture'>Recreation and Culture</MenuItem>
                                <MenuItem value='Social and Health Care Services'>Social and Health Care Services</MenuItem>
                                <MenuItem value='Transportation Services'>Transportation Services</MenuItem>
                                <MenuItem value='Vehicles and Equipment'>Vehicles and Equipment</MenuItem>
                            </Select>
                        </LabelledField>
                        <LabelledField label={t("commons.subClassification")} className='labelField document-edit-panel'>
                            <TextField
                                onChange={onChangeSubClassification}
                                id="document-sub_classification"
                                fullWidth
                                variant="outlined"
                                placeholder='Add a sub-classification'
                                value={sub_classification}
                            />
                        </LabelledField>
                        <LabelledField label={t("commons.owner")} className='labelField document-edit-panel'>
                            <Select
                                id='document-owner'
                                fullWidth
                                variant="outlined"
                                value={owner}
                                onChange={onChangeOwner}
                                displayEmpty
                                renderValue={(selected) => {
                                    if (selected.length === 0) {
                                        return <span className="select-placeholder-css">Select an owner</span>;
                                    }
                                    return selected;
                                }}
                            >
                                <MenuItem value='Clerks Office'>Clerks Office</MenuItem>
                                <MenuItem value='Community Planning'>Community Planning</MenuItem>
                                <MenuItem value='Development Planning'>Development Planning</MenuItem>
                                <MenuItem value='Development Engineering'>Development Engineering</MenuItem>
                                <MenuItem value='Utilities'>Utilities</MenuItem>
                                <MenuItem value='Engineering Operations'>Engineering Operations</MenuItem>
                                <MenuItem value='Parks'>Parks</MenuItem>
                                <MenuItem value='Environment'>Environment</MenuItem>
                                <MenuItem value='Facilities'>Facilities</MenuItem>
                                <MenuItem value='Humam Resources'>Humam Resources</MenuItem>
                                <MenuItem value='Review and Compliance'>Review and Compliance</MenuItem>
                                <MenuItem value='Climate and Biodiversity'>Climate and Biodiversity</MenuItem>
                                <MenuItem value='Communications'>Communications</MenuItem>
                                <MenuItem value='Finance'>Finance</MenuItem>
                                <MenuItem value='Fire Services'>Fire Services</MenuItem>
                                <MenuItem value='IT Services'>IT Services</MenuItem>
                                <MenuItem value='Solicitor'>Solicitor</MenuItem>
                                <MenuItem value='GIS'>GIS</MenuItem>
                                <MenuItem value='Real Estate'>Real Estate</MenuItem>
                                <MenuItem value='Bylaw Services'>Bylaw Services</MenuItem>
                                <MenuItem value='Planning'>Planning</MenuItem>
                                <MenuItem value='External: Library Board'>External: Library Board</MenuItem>
                                <MenuItem value='External: NS Health'>External: NS Health</MenuItem>
                                <MenuItem value='External: RCMP'>External: RCMP</MenuItem>
                                <MenuItem value='External: NVRC'>External: NVRC</MenuItem>
                                <MenuItem value='External: Museum and Arch'>External: Museum and Arch</MenuItem>
                                <MenuItem value='External: Other'>External: Other</MenuItem>
                            </Select>
                        </LabelledField>
                        <LabelledField label={t("commons.documentReference")} className='labelField document-edit-panel'>
                            <div className="form-id-area guid-text-field">
                                <p>{_doc_id}</p>
                                <Button
                                    className="copyButton doc-edit-details"
                                    startIcon={<SVGIcons.common.Copy/>}
                                    onClick={onCopyToClipbaord}
                                />
                            </div>
                        </LabelledField>
                        <LabelledField label={t("commons.externalSourceDocumentId")} className='labelField document-edit-panel'>
                            <TextField
                                onChange={onChangeExternalId}
                                id="document-external_id"
                                fullWidth
                                variant="outlined"
                                value={external_id}
                                placeholder='Add a document ID'
                            />
                        </LabelledField>
                        <LabelledField label={t("commons.retentionPeriod")} className='labelField document-edit-panel'>
                            <Select
                                id='document-retention_months'
                                fullWidth
                                variant="outlined"
                                value={retention_months}
                                onChange={onChangeRetentionMonths}
                            >
                                <MenuItem value='6'>06 months</MenuItem>
                                <MenuItem value='12'>12 months</MenuItem>
                                <MenuItem value='18'>18 months</MenuItem>
                                <MenuItem value='24'>24 months</MenuItem>
                                <MenuItem value='30'>30 months</MenuItem>
                                <MenuItem value='36'>36 months</MenuItem>
                                <MenuItem value='42'>42 months</MenuItem>
                                <MenuItem value='48'>48 months</MenuItem>
                                <MenuItem value='54'>54 months</MenuItem>
                                <MenuItem value='60'>60 months</MenuItem>
                                <MenuItem value='66'>66 months</MenuItem>
                                <MenuItem value='72'>72 months</MenuItem>
                                <MenuItem value='78'>78 months</MenuItem>
                                <MenuItem defaultChecked value='84'>84 months</MenuItem>
                                <MenuItem value='90'>90 months</MenuItem>
                                <MenuItem value='96'>96 months</MenuItem>
                                <MenuItem value='102'>102 months</MenuItem>
                                <MenuItem value='108'>108 months</MenuItem>
                                <MenuItem value='114'>114 months</MenuItem>
                                <MenuItem value='120'>120 months</MenuItem>
                            </Select>
                        </LabelledField>                     
                    </>
                )}
            </TabPanel>
        </Fragment>
    );
};
export { DocumentEditRecordsManagementPanel };
